// lib
import { List } from 'immutable'
import { Modifier, ContentBlock, ContentState, EditorState, genKey } from 'draft-js'
import { removeSelectedContentFromEditorState } from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/editor-tools'

// helpers
export function buildMultilineText(lines = [], maxLines = 0, lineBreak = '\n', linesJoint = ' ') {
  return lines.slice(0, maxLines).join(lineBreak) + linesJoint + lines.slice(maxLines, lines.length).join(linesJoint)
}

// plugin
export default function multilineLimitPlugin(config = {}) {
  const { maxLines = 1, separator = '\n' } = config

  return {
    handleKeyCommand(command, _editorState, _eventTimeStamp, { setEditorState }) {
      switch (command) {
        case 'split-block': {
          const editorState = removeSelectedContentFromEditorState(_editorState)
          const contentState = editorState.getCurrentContent()
          const editorStateText = contentState.getPlainText(separator) || ''
          const lines = editorStateText.split(separator)
          if (lines.length < maxLines) {
            const selection = editorState.getSelection()
            const afterSelectionText = editorStateText.slice(selection.getFocusOffset())
            // Firefox fix: add space at the end
            const applyFFFix = 0 == afterSelectionText.length
            const dividerText = separator + (applyFFFix ? ' ' : '')
            const newContentState = Modifier.insertText(
              contentState,
              selection,
              dividerText,
              editorState.getCurrentInlineStyle(),
              null
            )
            const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters')
            const curFocusOffset = newEditorState.getSelection().getFocusOffset()
            const newSelection = applyFFFix
              ? newEditorState.getSelection().merge({
                  focusOffset: curFocusOffset - 1,
                  anchorOffset: curFocusOffset - 1
                })
              : newContentState.getSelectionAfter()

            setEditorState(EditorState.forceSelection(newEditorState, newSelection))
          }
          return 'handled'
        }
        default: {
          return 'not-handled'
        }
      }
    },
    onChange: function onChange(editorState) {
      const blocks = editorState.getCurrentContent().getBlocksAsArray()
      if (blocks.length > 1) {
        // Gather all the blocks texts
        const { lines, characterList } = blocks.reduce(
          ({ lines, characterList }, block) => {
            if (block.getType() !== 'atomic') {
              lines.push(...block.getText().split(separator))
              return {
                lines,
                characterList: characterList.concat(block.getCharacterList())
              }
            }
            return { lines, characterList }
          },
          { lines: [], characterList: List() }
        )

        // Create a new content block with multiline text
        const contentBlock = new ContentBlock({
          key: genKey(),
          text: buildMultilineText(lines, maxLines, separator),
          characterList,
          type: 'unstyled',
          depth: 0
        })

        // Update the editor state with the limited multiline text
        const newContentState = ContentState.createFromBlockArray([contentBlock])
        const updatedEditorState = EditorState.push(editorState, newContentState, 'remove-range')
        return EditorState.moveFocusToEnd(updatedEditorState)
      }

      // We only have one content block
      const contentBlock = blocks[0]
      const text = contentBlock.getText() || ''
      const lines = text.split(separator)
      if (maxLines < lines.length) {
        // Create a new content block with multiline text
        const newContentBlock = new ContentBlock({
          key: genKey(),
          text: buildMultilineText(lines, maxLines, separator),
          characterList: contentBlock.getCharacterList(),
          type: 'unstyled',
          depth: 0
        })

        // Update the editor state with the limited multiline text
        const editorStateSelection = editorState.getSelection()
        const newContentState = ContentState.createFromBlockArray([newContentBlock])

        return (editorStateSelection.getHasFocus() ? EditorState.moveFocusToEnd : EditorState.moveSelectionToEnd)(
          EditorState.push(editorState, newContentState, 'insert-characters')
        )
      }

      return editorState
    }
  }
}
